"use client";

import Link from "next/link";
import type { CalculatorMeta } from "@/lib/calculators-data";
import Icon from "@/components/Icon";

interface CalculatorCardProps {
  calculator: CalculatorMeta;
}

function getCategoryInfo(id: string, isDiy: boolean) {
  if (isDiy) {
    return {
      label: "Material",
      colorVar: "var(--card-accent-diy)",
      tintVar: "var(--card-tint-diy)"
    };
  }
  if (["kitchen", "bathroom", "roi"].includes(id)) {
    return {
      label: "Interior cost",
      colorVar: "var(--card-accent-interior)",
      tintVar: "var(--card-tint-interior)"
    };
  }
  if (["roofing", "windows", "deck"].includes(id)) {
    return {
      label: "Exterior cost",
      colorVar: "var(--card-accent-exterior)",
      tintVar: "var(--card-tint-exterior)"
    };
  }
  if (["solar", "hvac"].includes(id)) {
    return {
      label: "Systems & energy",
      colorVar: "var(--card-accent-systems)",
      tintVar: "var(--card-tint-systems)"
    };
  }
  return {
    label: "Cost model",
    colorVar: "var(--accent)",
    tintVar: "var(--accent-tint)"
  };
}

export default function CalculatorCard({ calculator }: CalculatorCardProps) {
  const catInfo = getCategoryInfo(calculator.id, calculator.category === "diy");

  return (
    <Link
      href={`/calculators/${calculator.slug}/`}
      id={`calc-card-${calculator.id}`}
      className="card-blueprint group flex flex-col p-5 hover:border-[var(--accent)] transition-all duration-150 text-left h-full shadow-none bg-[var(--surface)]"
    >
      {/* Category Eyebrow */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-[8.5px] font-mono font-bold tracking-widest text-[var(--ink-soft)] uppercase">
          {calculator.category === "diy" ? "Material Estimator" : "Cost Estimator"}
        </span>
      </div>

      {/* Title & Icon Header */}
      <div className="flex items-start gap-3.5 mb-3.5">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-sm transition-transform group-hover:scale-105"
          style={{ backgroundColor: catInfo.tintVar, color: catInfo.colorVar }}
        >
          <Icon name={calculator.icon} className="w-4.5 h-4.5" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-extrabold text-[var(--ink)] group-hover:text-[var(--accent)] tracking-tight font-sans uppercase leading-[1.2] transition-colors break-words">
            {calculator.name}
          </h3>
          <span
            className="text-[11px] font-sans mt-1 block font-normal"
            style={{ color: catInfo.colorVar }}
          >
            {catInfo.label}
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-[var(--ink-soft)] text-[12px] leading-relaxed mb-5 flex-1 font-sans">
        {calculator.description}
      </p>

      {/* Launch chevron CTA */}
      <div className="text-[9.5px] font-bold text-[var(--ink-soft)] group-hover:text-[var(--accent)] uppercase tracking-widest flex items-center gap-1.5 mt-auto transition-colors duration-150 font-mono">
        <span>Launch Calculator</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-1 transition-transform">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}
